// Top-up Service for Main App
// Handles loading top-ups from Firebase for the main app

import { 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy 
} from 'firebase/firestore';
import { db } from '../firebase.config';

// Collection reference
const TOPUP_CATALOG_COLLECTION = 'topup_catalog';

/**
 * Get all active top-ups from catalog
 * @returns {Promise<Array>} Array of top-up objects
 */
export const getAllTopups = async () => {
  try {
    const topupsRef = collection(db, TOPUP_CATALOG_COLLECTION);
    const q = query(topupsRef, where('isActive', '==', true), orderBy('displayOrder', 'asc'));
    const querySnapshot = await getDocs(q);
    
    const topups = [];
    querySnapshot.forEach((doc) => {
      topups.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return topups;
  } catch (error) {
    console.error('Error fetching top-ups:', error);
    // Fallback to hardcoded catalog if Firebase fails
    return getFallbackTopups();
  }
};

/**
 * Get top-ups by category
 * @param {string} category - Category name
 * @returns {Promise<Array>} Array of top-up objects
 */
export const getTopupsByCategory = async (category) => {
  try {
    const topupsRef = collection(db, TOPUP_CATALOG_COLLECTION);
    const q = query(
      topupsRef, 
      where('category', '==', category),
      where('isActive', '==', true),
      orderBy('displayOrder', 'asc')
    );
    const querySnapshot = await getDocs(q);
    
    const topups = [];
    querySnapshot.forEach((doc) => {
      topups.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return topups;
  } catch (error) {
    console.error('Error fetching top-ups by category:', error);
    // Fallback to hardcoded catalog
    const fallbackTopups = getFallbackTopups();
    return fallbackTopups.filter(topup => topup.category === category);
  }
};

/**
 * Get popular top-ups
 * @returns {Promise<Array>} Array of popular top-up objects
 */
export const getPopularTopups = async () => {
  try {
    const topupsRef = collection(db, TOPUP_CATALOG_COLLECTION);
    const q = query(
      topupsRef, 
      where('popular', '==', true),
      where('isActive', '==', true),
      orderBy('displayOrder', 'asc')
    );
    const querySnapshot = await getDocs(q);
    
    const topups = [];
    querySnapshot.forEach((doc) => {
      topups.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return topups;
  } catch (error) {
    console.error('Error fetching popular top-ups:', error);
    // Fallback to hardcoded catalog
    const fallbackTopups = getFallbackTopups();
    return fallbackTopups.filter(topup => topup.popular);
  }
};

/**
 * Get top-up by ID
 * @param {string} topupId - Top-up ID
 * @returns {Promise<Object|null>} Top-up object or null
 */
export const getTopupById = async (topupId) => {
  try {
    const topups = await getAllTopups();
    return topups.find(topup => topup.id === topupId) || null;
  } catch (error) {
    console.error('Error fetching top-up by ID:', error);
    // Fallback to hardcoded catalog
    const fallbackTopups = getFallbackTopups();
    return fallbackTopups.find(topup => topup.id === topupId) || null;
  }
};

/**
 * Get top-up categories
 * @returns {Promise<Array>} Array of unique categories
 */
export const getTopupCategories = async () => {
  try {
    const topups = await getAllTopups();
    const categories = [...new Set(topups.map(topup => topup.category))];
    return categories.sort();
  } catch (error) {
    console.error('Error fetching top-up categories:', error);
    // Fallback to hardcoded catalog
    const fallbackTopups = getFallbackTopups();
    const categories = [...new Set(fallbackTopups.map(topup => topup.category))];
    return categories.sort();
  }
};

/**
 * Fallback hardcoded top-ups (same as the constants file)
 * Used when Firebase is unavailable
 */
const getFallbackTopups = () => {
  return [
    {
      id: 'operations_mini',
      name: 'Operations Mini Pack',
      description: 'Additional 1,000 operations for immediate use',
      price: 99,
      currency: '₹',
      category: 'operations',
      displayOrder: 1,
      isActive: true,
      limits: {
        firebase_operations: 1000,
        storage_mb: 0,
        sales_documents: 0,
        parties: 0
      },
      features: [
        '1,000 additional operations',
        'Till-use (never expires)',
        'Instant activation'
      ],
      popular: false
    },
    {
      id: 'operations_standard',
      name: 'Operations Standard Pack',
      description: 'Additional 5,000 operations for heavy usage',
      price: 399,
      currency: '₹',
      category: 'operations',
      displayOrder: 2,
      isActive: true,
      limits: {
        firebase_operations: 5000,
        storage_mb: 0,
        sales_documents: 0,
        parties: 0
      },
      features: [
        '5,000 additional operations',
        'Till-use (never expires)',
        'Best value for money',
        'Instant activation'
      ],
      popular: true
    },
    {
      id: 'operations_mega',
      name: 'Operations Mega Pack',
      description: 'Additional 15,000 operations for power users',
      price: 999,
      currency: '₹',
      category: 'operations',
      displayOrder: 3,
      isActive: true,
      limits: {
        firebase_operations: 15000,
        storage_mb: 0,
        sales_documents: 0,
        parties: 0
      },
      features: [
        '15,000 additional operations',
        'Till-use (never expires)',
        'Maximum value',
        'Instant activation'
      ],
      popular: false
    },
    {
      id: 'storage_basic',
      name: 'Storage Basic Pack',
      description: 'Additional 500MB storage space',
      price: 199,
      currency: '₹',
      category: 'storage',
      displayOrder: 4,
      isActive: true,
      limits: {
        firebase_operations: 0,
        storage_mb: 500,
        sales_documents: 0,
        parties: 0
      },
      features: [
        '500MB additional storage',
        'Till-use (never expires)',
        'Instant activation'
      ],
      popular: false
    },
    {
      id: 'storage_premium',
      name: 'Storage Premium Pack',
      description: 'Additional 2GB storage space',
      price: 599,
      currency: '₹',
      category: 'storage',
      displayOrder: 5,
      isActive: true,
      limits: {
        firebase_operations: 0,
        storage_mb: 2048,
        sales_documents: 0,
        parties: 0
      },
      features: [
        '2GB additional storage',
        'Till-use (never expires)',
        'Best value for storage',
        'Instant activation'
      ],
      popular: true
    },
    {
      id: 'documents_basic',
      name: 'Documents Basic Pack',
      description: 'Additional 1,000 sales documents',
      price: 299,
      currency: '₹',
      category: 'documents',
      displayOrder: 6,
      isActive: true,
      limits: {
        firebase_operations: 0,
        storage_mb: 0,
        sales_documents: 1000,
        parties: 0
      },
      features: [
        '1,000 additional sales documents',
        'Till-use (never expires)',
        'Instant activation'
      ],
      popular: false
    },
    {
      id: 'documents_premium',
      name: 'Documents Premium Pack',
      description: 'Additional 5,000 sales documents',
      price: 1199,
      currency: '₹',
      category: 'documents',
      displayOrder: 7,
      isActive: true,
      limits: {
        firebase_operations: 0,
        storage_mb: 0,
        sales_documents: 5000,
        parties: 0
      },
      features: [
        '5,000 additional sales documents',
        'Till-use (never expires)',
        'Best value for documents',
        'Instant activation'
      ],
      popular: true
    },
    {
      id: 'parties_basic',
      name: 'Parties Basic Pack',
      description: 'Additional 500 parties/customers',
      price: 199,
      currency: '₹',
      category: 'parties',
      displayOrder: 8,
      isActive: true,
      limits: {
        firebase_operations: 0,
        storage_mb: 0,
        sales_documents: 0,
        parties: 500
      },
      features: [
        '500 additional parties/customers',
        'Till-use (never expires)',
        'Instant activation'
      ],
      popular: false
    },
    {
      id: 'parties_premium',
      name: 'Parties Premium Pack',
      description: 'Additional 2,000 parties/customers',
      price: 699,
      currency: '₹',
      category: 'parties',
      displayOrder: 9,
      isActive: true,
      limits: {
        firebase_operations: 0,
        storage_mb: 0,
        sales_documents: 0,
        parties: 2000
      },
      features: [
        '2,000 additional parties/customers',
        'Till-use (never expires)',
        'Best value for parties',
        'Instant activation'
      ],
      popular: true
    },
    {
      id: 'combo_operations_storage',
      name: 'Operations + Storage Combo',
      description: '3,000 operations + 1GB storage',
      price: 799,
      currency: '₹',
      category: 'combo',
      displayOrder: 10,
      isActive: true,
      limits: {
        firebase_operations: 3000,
        storage_mb: 1024,
        sales_documents: 0,
        parties: 0
      },
      features: [
        '3,000 additional operations',
        '1GB additional storage',
        'Till-use (never expires)',
        'Save ₹200 vs buying separately',
        'Instant activation'
      ],
      popular: true
    },
    {
      id: 'combo_business_boost',
      name: 'Business Boost Pack',
      description: 'Complete business solution with all resources',
      price: 1999,
      currency: '₹',
      category: 'combo',
      displayOrder: 11,
      isActive: true,
      limits: {
        firebase_operations: 10000,
        storage_mb: 2048,
        sales_documents: 2000,
        parties: 1000
      },
      features: [
        '10,000 additional operations',
        '2GB additional storage',
        '2,000 additional sales documents',
        '1,000 additional parties',
        'Till-use (never expires)',
        'Save ₹1,000 vs buying separately',
        'Instant activation'
      ],
      popular: true
    },
    {
      id: 'enterprise_operations',
      name: 'Enterprise Operations Pack',
      description: '50,000 operations for enterprise users',
      price: 3999,
      currency: '₹',
      category: 'enterprise',
      displayOrder: 12,
      isActive: true,
      limits: {
        firebase_operations: 50000,
        storage_mb: 0,
        sales_documents: 0,
        parties: 0
      },
      features: [
        '50,000 additional operations',
        'Till-use (never expires)',
        'Enterprise-grade capacity',
        'Instant activation',
        'Priority support'
      ],
      popular: false
    },
    {
      id: 'enterprise_storage',
      name: 'Enterprise Storage Pack',
      description: '10GB storage for enterprise users',
      price: 2999,
      currency: '₹',
      category: 'enterprise',
      displayOrder: 13,
      isActive: true,
      limits: {
        firebase_operations: 0,
        storage_mb: 10240,
        sales_documents: 0,
        parties: 0
      },
      features: [
        '10GB additional storage',
        'Till-use (never expires)',
        'Enterprise-grade capacity',
        'Instant activation',
        'Priority support'
      ],
      popular: false
    }
  ];
};

// Export all functions
export default {
  getAllTopups,
  getTopupsByCategory,
  getPopularTopups,
  getTopupById,
  getTopupCategories
};
















































